import React, { memo, useEffect, useRef } from 'react'
import { Button, Icon, Spin } from 'antd'
import { ImagePreview } from '../../../../../../components'
import { COLORS } from './utils'

const MAX_VISIBLE_REFERENCE_THUMBNAILS = 8

const ConversationList = memo(
  ({
    messages,
    currentImage,
    initialPrompt,
    selectedForSave,
    aspectRatio,
    onAnnotateCurrent,
    onUseImage
  }) => {
    const endRef = useRef(null)

    useEffect(() => {
      if (endRef.current) {
        endRef.current.scrollIntoView({ block: 'end' })
      }
    }, [messages.length])

    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '24px 28px',
          background: '#f9f9f9',
          borderRadius: 12
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.textMuted,
              textAlign: 'center'
            }}
          >
            <div
              style={{
                height: 48,
                width: 48,
                background: COLORS.primary,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16
              }}
            >
              <Icon type="file-image" style={{ color: '#fff', fontSize: 22 }} />
            </div>
            <div style={{ fontSize: 16, marginBottom: 16 }}>
              Describe the edit you want
            </div>
            {currentImage && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: 12,
                  background: COLORS.white,
                  borderRadius: 8,
                  border: `1px solid ${COLORS.borderLight}`
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    color: COLORS.textMuted
                  }}
                >
                  Image being edited
                </div>
                <div style={{ position: 'relative' }}>
                  <ImagePreview
                    src={currentImage}
                    alt="Current"
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 8,
                      display: 'block'
                    }}
                  />
                  <Button
                    size="small"
                    shape="circle"
                    icon="highlight"
                    onClick={onAnnotateCurrent}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(255,255,255,0.92)',
                      border: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  />
                </div>
                {initialPrompt ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: COLORS.textSecondary,
                      fontStyle: 'italic',
                      maxWidth: 300
                    }}
                  >
                    "{initialPrompt}"
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          messages.map(message => (
            <div key={message.id} style={{ marginBottom: 20 }}>
              {message.type === 'user' ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div
                    style={{
                      maxWidth: '70%',
                      background: COLORS.primaryLight,
                      padding: '12px 16px',
                      borderRadius: '16px 16px 4px 16px',
                      color: COLORS.text,
                      lineHeight: 1.5
                    }}
                  >
                    <div>{message.content}</div>
                    {message.references?.length ? (
                      <div
                        style={{
                          marginTop: 10,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-end',
                          flexWrap: 'wrap',
                          gap: 6,
                          maxWidth: 300
                        }}
                      >
                        {message.references
                          .slice(0, MAX_VISIBLE_REFERENCE_THUMBNAILS)
                          .map((referenceUrl, index) => (
                            <ImagePreview
                              key={`${referenceUrl}-${index}`}
                              src={referenceUrl}
                              alt={
                                index === 0
                                  ? 'Current reference'
                                  : `Reference ${index + 1}`
                              }
                              style={{
                                width: 44,
                                height: 44,
                                objectFit: 'cover',
                                borderRadius: 6,
                                border:
                                  index === 0
                                    ? `2px solid ${COLORS.primary}`
                                    : `1px solid ${COLORS.borderLight}`,
                                background: COLORS.background,
                                display: 'block'
                              }}
                            />
                          ))}
                        {message.references.length > MAX_VISIBLE_REFERENCE_THUMBNAILS ? (
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 6,
                              border: `1px solid ${COLORS.borderLight}`,
                              background: 'rgba(80, 87, 213, 0.08)',
                              color: COLORS.textSecondary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                          >
                            +
                            {message.references.length -
                              MAX_VISIBLE_REFERENCE_THUMBNAILS}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {message.type === 'loading' ? (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    background: COLORS.white,
                    padding: '12px 16px',
                    borderRadius: '4px 16px 16px 16px',
                    border: `1px solid ${COLORS.borderLight}`
                  }}
                >
                  <Spin size="small" />
                  <span style={{ color: COLORS.textSecondary }}>
                    Generating...
                  </span>
                </div>
              ) : null}

              {message.type === 'assistant' ? (
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    gap: 10,
                    background: COLORS.white,
                    padding: 12,
                    borderRadius: '4px 16px 16px 16px',
                    border: `1px solid ${COLORS.border}`
                  }}
                >
                  <ImagePreview
                    src={message.content}
                    alt="Generated"
                    style={{
                      width: 260,
                      aspectRatio: aspectRatio
                        ? aspectRatio.replace(':', '/')
                        : '1/1',
                      objectFit: 'cover',
                      borderRadius: 8,
                      display: 'block'
                    }}
                  />
                  <Button
                    type={
                      selectedForSave === message.content
                        ? 'primary'
                        : 'default'
                    }
                    icon={
                      selectedForSave === message.content
                        ? 'check'
                        : 'plus'
                    }
                    onClick={() => onUseImage(message.content)}
                    style={{
                      alignSelf: 'flex-start',
                      background:
                        selectedForSave === message.content
                          ? COLORS.primary
                          : undefined,
                      borderColor:
                        selectedForSave === message.content
                          ? COLORS.primary
                          : undefined
                    }}
                  >
                    {selectedForSave === message.content
                      ? 'Selected'
                      : 'Use this image'}
                  </Button>
                </div>
              ) : null}

              {message.type === 'error' ? (
                <div
                  style={{
                    display: 'inline-block',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '12px 16px',
                    borderRadius: '4px 16px 16px 16px'
                  }}
                >
                  {message.content}
                </div>
              ) : null}
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
    )
  }
)

export default ConversationList
